import React, { useState } from 'react';
import {
  FaWrench,
  FaTruck,
  FaCar,
  FaKey,
  FaGasPump,
  FaBolt,
  FaWhatsapp
} from 'react-icons/fa';
import { validateCPF, formatCPF } from '../utils/cpfValidator';
import './Scheduling.css';

function Scheduling() {
  const [activeForm, setActiveForm] = useState('installation');
  
  // Estado para controlar o envio dos formulários
  const [isSubmitting, setIsSubmitting] = useState({
    installation: false,
    assistance: false
  });
  
  const [formData, setFormData] = useState({
    // Dados comuns
    name: '',
    phone: '',
    email: '',
    cpf: '',
    rg: '',
    birthDate: '',
    
    // Estados de validação
    cpfTouched: false,

    // Dados de endereço
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',

    // Dados específicos para instalação
    vehicles: [{
      id: 1,
      vehicleModel: '',
      vehicleYear: '',
      vehiclePlate: '',
      vehicleColor: '',
      vehicleChassis: '',
      serviceType: ''
    }],
    installationAddress: '',
    installAtHeadquarters: false,
    referralSource: '',
    isLawEnforcement: false,
    lawEnforcementDetails: '',
    preferredDate: '',
    preferredTime: '',

    // Dados específicos para assistência
    location: '',
    destinationAddress: '',
    problemDescription: '',
    serviceType: '' // Mantido apenas para o formulário de assistência
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'installAtHeadquarters' && checked ? { installationAddress: '' } : {}),
      ...(name === 'isLawEnforcement' && !checked ? { lawEnforcementDetails: '' } : {})
    }));
  };

  const handleVehicleInputChange = (index, field, value) => {
    setFormData(prevState => ({
      ...prevState,
      vehicles: prevState.vehicles.map((vehicle, i) => 
        i === index ? { ...vehicle, [field]: value } : vehicle
      )
    }));
  };

  const addVehicle = () => {
    setFormData(prevState => ({
      ...prevState,
      vehicles: [...prevState.vehicles, {
        id: prevState.vehicles.length + 1,
        vehicleModel: '',
        vehicleYear: '',
        vehiclePlate: '',
        vehicleColor: '',
        vehicleChassis: '',
        serviceType: ''
      }]
    }));
  };

  const removeVehicle = (index) => {
    if (formData.vehicles.length > 1) {
      setFormData(prevState => ({
        ...prevState,
        vehicles: prevState.vehicles.filter((_, i) => i !== index)
      }));
    }
  };

  // Função para lidar com mudanças no campo CPF
  const handleCPFChange = (e) => {
    const value = e.target.value;
    const formattedValue = formatCPF(value);
    
    setFormData(prevState => ({
      ...prevState,
      cpf: formattedValue,
      cpfTouched: true
    }));
  };

  const handleCepChange = async (e) => {
    const value = e.target.value.replace(/\D/g, '');
    
    // Atualiza o valor do CEP no estado
    setFormData(prevState => ({
      ...prevState,
      cep: value
    }));

    // Só busca o endereço se tiver 8 dígitos
    if (value.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${value}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setFormData(prevState => ({
            ...prevState,
            street: data.logradouro,
            neighborhood: data.bairro,
            city: data.localidade,
            state: data.uf
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  // Converte yyyy-mm-dd (input date) para dd/mm/yyyy
  const formatDateBR = (dateValue) => {
    if (!dateValue) return '';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) return dateValue;
    const match = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return dateValue;
    return `${match[3]}/${match[2]}/${match[1]}`;
  };

  const handleSubmit = async (e, formType) => {
    e.preventDefault();
    
    // Prevenir múltiplos envios
    if (isSubmitting[formType]) {
      return;
    }
    
    // Ativar estado de loading
    setIsSubmitting(prev => ({ ...prev, [formType]: true }));
    
    // Validação de CPF para formulário de instalação
    if (formType === 'installation' && formData.cpf && !validateCPF(formData.cpf)) {
      alert('Por favor, verifique se o CPF informado está correto antes de continuar.');
      setIsSubmitting(prev => ({ ...prev, [formType]: false }));
      return;
    }

    // Validação de data e horário preferenciais
    if (formType === 'installation' && (!formData.preferredDate || !formData.preferredTime)) {
      alert('Por favor, informe a data e o horário preferenciais antes de enviar.');
      setIsSubmitting(prev => ({ ...prev, [formType]: false }));
      return;
    }
    
    const endpoint = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/api/scheduling/submit-form`;
    const token = '100321Gr';

    // Para o formulário de assistência, vamos usar o nome do serviço diretamente
    let serviceTypeName = '';
    if (formType === 'assistance') {
      const selectedService = assistanceServices.find(service => service.id === formData.serviceType);
      serviceTypeName = selectedService ? selectedService.name : '';
    }

    // Organize data to match exactly the Google Sheets structure
    const requestData = {
      token,
      formType,
      formData: formType === 'assistance' ? {
        // Ordem exata conforme saveAssistanceToSheet
        name: formData.name || '',                    // Nome
        phone: formData.phone || '',                  // Telefone
        email: formData.email || '',                  // E-mail
        vehiclePlate: formData.vehiclePlate || '',    // Placa do Veículo
        location: formData.location || '',            // Localização Atual
        serviceType: serviceTypeName || '',           // Tipo de Serviço
        destinationAddress: formData.destinationAddress || '',  // Endereço de Destino
        problemDescription: formData.problemDescription || ''   // Descrição do Problema
      } : {
        // Ordem exata conforme saveInstallationToSheet
        name: formData.name || '',                    // Nome
        phone: formData.phone || '',                  // Telefone
        email: formData.email || '',                  // E-mail
        cpf: formData.cpf || '',                     // CPF
        rg: formData.rg || '',                       // RG
        birthDate: formatDateBR(formData.birthDate), // Data de Nascimento (dd/mm/yyyy)
        // Dados de endereço
        cep: formData.cep || '',                     // CEP
        street: formData.street || '',               // Rua
        number: formData.number || '',               // Número
        complement: formData.complement || '',        // Complemento
        neighborhood: formData.neighborhood || '',    // Bairro
        city: formData.city || '',                   // Cidade
        state: formData.state || '',                 // Estado
        // Dados dos veículos
        vehicles: formData.vehicles.map(vehicle => ({
          ...vehicle,
          serviceType: serviceTypes.find(service => service.id === vehicle.serviceType)?.name || ''
        })),
        installationAddress: formData.installationAddress || '', // Endereço de Instalação
        installAtHeadquarters: formData.installAtHeadquarters || false, // Instalar na Sede?
        referralSource: formData.referralSource || '', // Quem Indicou?
        isLawEnforcement: formData.isLawEnforcement || false, // É Agente da Segurança Pública?
        lawEnforcementDetails: formData.lawEnforcementDetails || '', // Detalhes do Agente
        preferredDate: formatDateBR(formData.preferredDate), // Data Preferencial (dd/mm/yyyy)
        preferredTime: formData.preferredTime || ''   // Horário Preferencial
      }
    };

    // Log removido por segurança - não expor dados sensíveis no console

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Erro na resposta:', errorData);
        throw new Error(`Erro na requisição: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        alert('Formulário enviado com sucesso!');
        // Limpa o formulário após o envio bem-sucedido
        setFormData({
          // Mantém apenas os campos necessários para o tipo de formulário atual
          name: '',
          phone: '',
          email: '',
          ...(formType === 'installation' ? {
            cpf: '',
            rg: '',
            birthDate: '',
            vehicles: [{
              id: 1,
              vehicleModel: '',
              vehicleYear: '',
              vehiclePlate: '',
              vehicleColor: '',
              vehicleChassis: '',
              serviceType: ''
            }],
            installationAddress: '',
            installAtHeadquarters: false,
            referralSource: '',
            isLawEnforcement: false,
            lawEnforcementDetails: '',
            preferredDate: '',
            preferredTime: ''
          } : {
            vehiclePlate: '',
            location: '',
            serviceType: '',
            destinationAddress: '',
            problemDescription: ''
          })
        });
      } else {
        throw new Error(result.message || 'Erro ao enviar formulário');
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      alert(`Erro ao enviar formulário: ${error.message}`);
    } finally {
      // Sempre desativar o estado de loading, independente do resultado
      setIsSubmitting(prev => ({ ...prev, [formType]: false }));
    }
  };

  const serviceTypes = [
    { id: 'rastreamento', name: 'Rastreamento Veicular' },
    { id: 'plano_moto_plus', name: 'Plano Moto +' },
    { id: 'rastreamento_corta_corrente', name: 'Rastreamento Veicular + Antifurto' },
    { id: 'assistencia_veicular', name: 'Assistência Veicular' },
  ];

  const lawEnforcementTypes = [
    { id: 'policial_civil', name: 'Policial Civil' },
    { id: 'policial_militar', name: 'Policial Militar' },
    { id: 'bombeiro_militar', name: 'Bombeiro Militar' },
    { id: 'guarda_municipal', name: 'Guarda Municipal (Policial Municipal)' },
    { id: 'policial_penal', name: 'Policial Penal' },
    { id: 'policial_federal', name: 'Policial Federal' },
    { id: 'policial_rodoviario_federal', name: 'Policial Rodoviário Federal' },
  ];

  const assistanceServices = [
    { id: 'reboque', name: 'Reboque', icon: <FaTruck /> },
    { id: 'borracheiro', name: 'Borracheiro', icon: <FaCar /> },
    { id: 'chaveiro', name: 'Chaveiro', icon: <FaKey /> },
    { id: 'pane_seca', name: 'Auxílio a Pane Seca', icon: <FaGasPump /> },
    { id: 'pane_eletrica', name: 'Auxílio a Pane Elétrica', icon: <FaBolt /> },
    { id: 'lavagem', name: 'Lavagem Automotiva', icon: <FaCar />, whatsapp: '+5585921547871' },
  ];

  return (
    <div className="scheduling-container">
      <div className="scheduling-hero">
        <div className="scheduling-hero-content">
          <h1>Agendamento</h1>
          <p>Agende seu serviço de forma rápida e prática</p>
        </div>
      </div>
      <div className="scheduling-content">
        <div className="form-selector" role="tablist" aria-label="Tipo de agendamento">
          <button
            type="button"
            role="tab"
            aria-selected={activeForm === 'installation'}
            className={`selector-button ${activeForm === 'installation' ? 'active' : ''}`}
            onClick={() => setActiveForm('installation')}
          >
            <FaWrench className="selector-icon" />
            <span className="selector-text">
              <strong>Instalação</strong>
              <small>Agende a instalação do rastreador</small>
            </span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeForm === 'assistance'}
            className={`selector-button ${activeForm === 'assistance' ? 'active' : ''}`}
            onClick={() => setActiveForm('assistance')}
          >
            <FaTruck className="selector-icon" />
            <span className="selector-text">
              <strong>Assistência</strong>
              <small>Solicite ajuda veicular</small>
            </span>
          </button>
        </div>

        {activeForm === 'installation' ? (
          <div className="form-section">
            <div className="form-section-header">
              <h2>Agende sua Instalação</h2>
              <p>Preencha os dados abaixo. Nossa equipe entrará em contato para confirmar o melhor horário.</p>
            </div>

            <form onSubmit={(e) => handleSubmit(e, 'installation')} className="scheduling-form">
              <div className="form-group personal-info-group form-panel">
                <div className="panel-heading">
                  <span className="panel-step">1</span>
                  <label className="section-label">Dados Pessoais</label>
                </div>
                <div className="personal-info-fields">
                  <div className="personal-field">
                    <label htmlFor="name">Nome Completo</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name || ''}
                      onChange={handleInputChange}
                      required
                      placeholder="Digite seu nome completo"
                    />
                  </div>

                  <div className="personal-field">
                    <label htmlFor="cpf">CPF</label>
                    <input
                      type="text"
                      id="cpf"
                      name="cpf"
                      value={formData.cpf || ''}
                      onChange={handleCPFChange}
                      onBlur={(e) => {
                        const cpf = e.target.value;
                        if (cpf && !validateCPF(cpf)) {
                          e.target.setCustomValidity('Por favor, verifique se o CPF está correto');
                        } else {
                          e.target.setCustomValidity('');
                        }
                      }}
                      required
                      placeholder="000.000.000-00"
                      maxLength="14"
                      pattern="\d{3}\.\d{3}\.\d{3}-\d{2}"
                      className={formData.cpfTouched && formData.cpf && !validateCPF(formData.cpf) ? 'error-field' : ''}
                    />
                    {formData.cpfTouched && formData.cpf && !validateCPF(formData.cpf) && (
                      <span className="error-message">
                        O CPF informado não é válido. Verifique se todos os 11 dígitos estão corretos.
                      </span>
                    )}
                  </div>

                  <div className="personal-field">
                    <label htmlFor="rg">RG</label>
                    <input
                      type="text"
                      id="rg"
                      name="rg"
                      value={formData.rg || ''}
                      onChange={handleInputChange}
                      required
                      placeholder="Digite seu RG"
                    />
                  </div>

                  <div className="personal-field">
                    <label htmlFor="birthDate">Data de Nascimento</label>
                    <input
                      type="date"
                      id="birthDate"
                      name="birthDate"
                      value={formData.birthDate || ''}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="personal-field">
                    <label htmlFor="phone">Telefone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone || ''}
                      onChange={handleInputChange}
                      required
                      placeholder="Digite seu telefone"
                    />
                  </div>

                  <div className="personal-field">
                    <label htmlFor="email">E-mail</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email || ''}
                      onChange={handleInputChange}
                      required
                      placeholder="Digite seu e-mail"
                    />
                  </div>
                </div>

                <div className="address-section">
                  <div className="address-row">
                    <div className="address-field cep-field">
                      <label htmlFor="cep">CEP</label>
                      <input
                        type="text"
                        id="cep"
                        name="cep"
                        value={formData.cep || ''}
                        onChange={handleCepChange}
                        placeholder="Digite o CEP"
                        maxLength="8"
                      />
                    </div>
                  </div>

                  <div className="address-row">
                    <div className="address-field street-field">
                      <label htmlFor="street">Rua</label>
                      <input
                        type="text"
                        id="street"
                        name="street"
                        value={formData.street || ''}
                        onChange={handleInputChange}
                        placeholder="Digite a rua"
                      />
                    </div>

                    <div className="address-field number-field">
                      <label htmlFor="number">Número</label>
                      <input
                        type="text"
                        id="number"
                        name="number"
                        value={formData.number || ''}
                        onChange={handleInputChange}
                        placeholder="Digite o número"
                      />
                    </div>
                  </div>

                  <div className="address-row">
                    <div className="address-field complement-field">
                      <label htmlFor="complement">Complemento</label>
                      <input
                        type="text"
                        id="complement"
                        name="complement"
                        value={formData.complement || ''}
                        onChange={handleInputChange}
                        placeholder="Digite o complemento"
                      />
                    </div>

                    <div className="address-field neighborhood-field">
                      <label htmlFor="neighborhood">Bairro</label>
                      <input
                        type="text"
                        id="neighborhood"
                        name="neighborhood"
                        value={formData.neighborhood || ''}
                        onChange={handleInputChange}
                        placeholder="Digite o bairro"
                      />
                    </div>
                  </div>

                  <div className="address-row">
                    <div className="address-field city-field">
                      <label htmlFor="city">Cidade</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city || ''}
                        onChange={handleInputChange}
                        placeholder="Digite a cidade"
                      />
                    </div>

                    <div className="address-field state-field">
                      <label htmlFor="state">UF</label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state || ''}
                        onChange={handleInputChange}
                        placeholder="Digite o estado"
                        maxLength="2"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group vehicle-info-group form-panel">
                <div className="panel-heading">
                  <span className="panel-step">2</span>
                  <label className="section-label">Dados do(s) Veículo(s)</label>
                </div>
                {formData.vehicles.map((vehicle, index) => (
                  <div key={vehicle.id} className="vehicle-section">
                    <div className="vehicle-header">
                      <h3>Veículo {index + 1}</h3>
                      {formData.vehicles.length > 1 && (
                        <button
                          type="button"
                          className="remove-vehicle-btn"
                          onClick={() => removeVehicle(index)}
                        >
                          Remover
                        </button>
                      )}
                    </div>
                    <div className="vehicle-info-fields">
                      <div className="vehicle-field">
                        <label htmlFor={`vehicleModel-${index}`}>Modelo do Veículo</label>
                        <input
                          type="text"
                          id={`vehicleModel-${index}`}
                          value={vehicle.vehicleModel}
                          onChange={(e) => handleVehicleInputChange(index, 'vehicleModel', e.target.value)}
                          required
                          placeholder="Digite o modelo do veículo"
                        />
                      </div>

                      <div className="vehicle-field">
                        <label htmlFor={`vehicleYear-${index}`}>Ano do Veículo</label>
                        <input
                          type="text"
                          id={`vehicleYear-${index}`}
                          value={vehicle.vehicleYear}
                          onChange={(e) => handleVehicleInputChange(index, 'vehicleYear', e.target.value)}
                          required
                          placeholder="Digite o ano do veículo"
                        />
                      </div>

                      <div className="vehicle-field">
                        <label htmlFor={`vehiclePlate-${index}`}>Placa do Veículo</label>
                        <input
                          type="text"
                          id={`vehiclePlate-${index}`}
                          value={vehicle.vehiclePlate}
                          onChange={(e) => handleVehicleInputChange(index, 'vehiclePlate', e.target.value)}
                          required
                          placeholder="Digite a placa do veículo"
                        />
                      </div>

                      <div className="vehicle-field">
                        <label htmlFor={`vehicleColor-${index}`}>Cor do Veículo</label>
                        <input
                          type="text"
                          id={`vehicleColor-${index}`}
                          value={vehicle.vehicleColor}
                          onChange={(e) => handleVehicleInputChange(index, 'vehicleColor', e.target.value)}
                          required
                          placeholder="Digite a cor do veículo"
                        />
                      </div>

                      <div className="vehicle-field">
                        <label htmlFor={`vehicleChassis-${index}`}>Chassi ou Renavam</label>
                        <input
                          type="text"
                          id={`vehicleChassis-${index}`}
                          value={vehicle.vehicleChassis}
                          onChange={(e) => handleVehicleInputChange(index, 'vehicleChassis', e.target.value)}
                          required
                          placeholder="Digite o chassi ou renavam"
                        />
                      </div>
                    </div>

                    <div className="vehicle-service-selection">
                      <label className="service-section-label">Qual serviço deseja para este veículo?</label>
                      <div className="service-options">
                        {serviceTypes.map(service => (
                          <div key={service.id} className="service-option">
                            <input
                              type="radio"
                              id={`${service.id}-${index}`}
                              name={`serviceType-${index}`}
                              value={service.id}
                              checked={vehicle.serviceType === service.id}
                              onChange={(e) => handleVehicleInputChange(index, 'serviceType', e.target.value)}
                              required
                            />
                            <label htmlFor={`${service.id}-${index}`}>{service.name}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
                
                <button
                  type="button"
                  className="add-vehicle-btn"
                  onClick={addVehicle}
                >
                  <span className="plus-icon">+</span>
                  Adicionar outro veículo
                </button>
              </div>

              <div className="form-group installation-location-group form-panel">
                <div className="panel-heading">
                  <span className="panel-step">3</span>
                  <label className="section-label">Local da Instalação</label>
                </div>
                <div className="installation-options">
                  <div className="checkbox-group headquarters-option">
                    <input
                      type="checkbox"
                      id="installAtHeadquarters"
                      name="installAtHeadquarters"
                      checked={formData.installAtHeadquarters || false}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="installAtHeadquarters">Instalar na SEDE (50% de desconto na adesão)</label>
                  </div>

                  {!formData.installAtHeadquarters && (
                    <div className="address-input">
                      <label htmlFor="installationAddress">Endereço para Instalação</label>
                      <input
                        type="text"
                        id="installationAddress"
                        name="installationAddress"
                        value={formData.installationAddress || ''}
                        onChange={handleInputChange}
                        required={!formData.installAtHeadquarters}
                        placeholder="Endereço completo com referência"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group referral-group form-panel">
                <div className="panel-heading">
                  <span className="panel-step">4</span>
                  <label className="section-label">Indicação</label>
                </div>
                <div className="referral-input">
                  <label htmlFor="referralSource">Quem lhe indicou?</label>
                  <input
                    type="text"
                    id="referralSource"
                    name="referralSource"
                    value={formData.referralSource || ''}
                    onChange={handleInputChange}
                    placeholder="Nome de quem indicou nossos serviços"
                  />
                </div>
              </div>

              <div className="form-group law-enforcement-section-group form-panel">
                <div className="panel-heading">
                  <span className="panel-step">5</span>
                  <label className="section-label">Agente de Segurança</label>
                </div>
                <div className="law-enforcement-section">
                  <div className="checkbox-group headquarters-option">
                    <input
                      type="checkbox"
                      id="isLawEnforcement"
                      name="isLawEnforcement"
                      checked={formData.isLawEnforcement || false}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="isLawEnforcement">Você é Agente da Segurança Pública?</label>
                  </div>

                  {formData.isLawEnforcement && (
                    <div className="law-enforcement-options">
                      {lawEnforcementTypes.map(type => (
                        <div key={type.id} className="law-enforcement-option">
                          <input
                            type="radio"
                            id={type.id}
                            name="lawEnforcementDetails"
                            value={type.name}
                            checked={formData.lawEnforcementDetails === type.name}
                            onChange={handleInputChange}
                            required={formData.isLawEnforcement}
                          />
                          <label htmlFor={type.id}>{type.name}</label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group scheduling-datetime-group form-panel form-panel-accent">
                <div className="panel-heading">
                  <span className="panel-step">6</span>
                  <label className="section-label">Data e Horário Preferenciais</label>
                </div>
                <div className="datetime-inputs">
                  <div className="datetime-field">
                    <label htmlFor="preferredDate">Data Preferencial</label>
                    <input
                      type="date"
                      id="preferredDate"
                      name="preferredDate"
                      value={formData.preferredDate || ''}
                      onChange={handleInputChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div className="datetime-field">
                    <label htmlFor="preferredTime">Horário Preferencial</label>
                    <input
                      type="time"
                      id="preferredTime"
                      name="preferredTime"
                      value={formData.preferredTime || ''}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <p className="form-trust-note">Após o envio, entraremos em contato para confirmar o agendamento.</p>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting.installation}
                >
                  {isSubmitting.installation ? (
                    <>
                      <span className="loading-spinner"></span>
                      Enviando...
                    </>
                  ) : (
                    'Agendar Instalação'
                  )}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="form-section">
            <div className="form-section-header">
              <h2>Assistência Veicular</h2>
              <p>Selecione o serviço desejado e preencha as informações para solicitar assistência.</p>
            </div>

            <div className="service-selector">
              {assistanceServices.map(service => (
                <button
                  key={service.id}
                  type="button"
                  className={`service-button ${formData.serviceType === service.id ? 'active' : ''}`}
                  data-whatsapp={service.whatsapp ? "true" : "false"}
                  onClick={() => {
                    if (service.whatsapp) {
                      const message = encodeURIComponent("Olá! Sou cliente da G3 Rastreamento e gostaria de agendar uma lavagem automotiva!");
                      window.open(`https://wa.me/${service.whatsapp}?text=${message}`, '_blank');
                    } else {
                      handleInputChange({ target: { name: 'serviceType', value: service.id } });
                    }
                  }}
                >
                  <span className="service-button-icon">{service.icon}</span>
                  <span className="service-button-label">{service.name}</span>
                  {service.whatsapp && <FaWhatsapp className="whatsapp-icon" />}
                </button>
              ))}
            </div>

            <form onSubmit={(e) => handleSubmit(e, 'assistance')} className="scheduling-form">
              <div className="form-group personal-info-group form-panel">
                <div className="panel-heading">
                  <span className="panel-step">1</span>
                  <label className="section-label">Dados Pessoais</label>
                </div>
                <div className="personal-info-fields">
                  <div className="personal-field">
                    <label htmlFor="name">Nome Completo</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name || ''}
                      onChange={handleInputChange}
                      required
                      disabled={!formData.serviceType}
                      placeholder="Digite seu nome completo"
                    />
                  </div>

                  <div className="personal-field">
                    <label htmlFor="phone">Telefone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone || ''}
                      onChange={handleInputChange}
                      required
                      disabled={!formData.serviceType}
                      placeholder="Digite seu telefone"
                    />
                  </div>

                  <div className="personal-field">
                    <label htmlFor="email">E-mail</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email || ''}
                      onChange={handleInputChange}
                      required
                      disabled={!formData.serviceType}
                      placeholder="Digite seu e-mail"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group vehicle-info-group form-panel">
                <div className="panel-heading">
                  <span className="panel-step">2</span>
                  <label className="section-label">Dados do Veículo</label>
                </div>
                <div className="vehicle-info-fields">
                  <div className="vehicle-field">
                    <label htmlFor="vehiclePlate">Placa do Veículo</label>
                    <input
                      type="text"
                      id="vehiclePlate"
                      name="vehiclePlate"
                      value={formData.vehiclePlate || ''}
                      onChange={handleInputChange}
                      required
                      disabled={!formData.serviceType}
                      placeholder="Digite a placa do veículo"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group location-info-group form-panel">
                <div className="panel-heading">
                  <span className="panel-step">3</span>
                  <label className="section-label">Localização</label>
                </div>
                <div className="location-info-fields">
                  <div className="location-field">
                    <label htmlFor="location">Localização Atual</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location || ''}
                      onChange={handleInputChange}
                      required
                      disabled={!formData.serviceType}
                      placeholder="Endereço completo com referência"
                    />
                  </div>

                  {formData.serviceType === 'reboque' && (
                    <div className="location-field">
                      <label htmlFor="destinationAddress">Endereço de Destino</label>
                      <input
                        type="text"
                        id="destinationAddress"
                        name="destinationAddress"
                        value={formData.destinationAddress || ''}
                        onChange={handleInputChange}
                        required
                        disabled={!formData.serviceType}
                        placeholder="Para onde o veículo deve ser levado?"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group problem-description-group form-panel">
                <div className="panel-heading">
                  <span className="panel-step">4</span>
                  <label className="section-label">Descrição do Problema</label>
                </div>
                <div className="problem-description-field">
                  <textarea
                    id="problemDescription"
                    name="problemDescription"
                    value={formData.problemDescription || ''}
                    onChange={handleInputChange}
                    required
                    disabled={!formData.serviceType}
                    rows="4"
                    placeholder="Descreva o problema em detalhes"
                  />
                </div>
              </div>

              <div className="form-actions">
                <p className="form-trust-note">Nossa equipe entrará em contato o mais rápido possível.</p>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={!formData.serviceType || isSubmitting.assistance}
                >
                  {isSubmitting.assistance ? (
                    <>
                      <span className="loading-spinner"></span>
                      Enviando...
                    </>
                  ) : (
                    'Solicitar Assistência'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default Scheduling;